import { type Accessor, createSignal, onMount } from "solid-js";

/**
 * Safety net against the router's "deferred flush" when loading a deep URL
 * (deep link).
 *
 * The problem: TanStack Router parses `location.search` asynchronously during
 * its first cycle. If a route component effect reads `useSearch()` and writes
 * back to the URL (with `navigate({ replace: true })`) BEFORE that first flush
 * has settled the initial params, it can overwrite the `?order=...` /
 * `?category=...` from the pasted link → the deep link is "lost" and the view
 * starts in its default state instead of the shared state.
 *
 * The defense: capture `location.search` SYNCHRONOUSLY on the first tick
 * (before any programmatic navigation) and expose it as a stable source of
 * truth. The route component can prefer this captured value over `useSearch()`
 * while the router still does not reflect it, and thus seed its local state
 * with what actually came in the URL. It is idempotent and read-only: it never
 * manipulates history.
 */

function parseSearch(raw: string): URLSearchParams {
  try {
    return new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  } catch {
    return new URLSearchParams();
  }
}

/**
 * Returns an accessor with a single `param` from the `location.search` captured
 * synchronously at mount. `undefined` if it was not in the initial URL.
 *
 * Typical use: seed a state signal (active order, category, filter) with the
 * deep-link value without depending on the router's first flush having already
 * settled the search params.
 */
export function createInitialSearchParam(param: string): Accessor<string | undefined> {
  // Synchronous capture on the first client tick. In SSR/without `window`,
  // return empty (state is reconstructed on the client).
  const captureNow = (): string | undefined => {
    const loc = globalThis.location;
    if (!loc) {
      return undefined;
    }
    const value = parseSearch(loc.search).get(param);
    return value && value.trim() !== "" ? value : undefined;
  };

  const [initial, setInitial] = createSignal<string | undefined>(captureNow());

  // Reaffirm after mount in case the synchronous capture ran before `location`
  // was available (hydration). Do not overwrite an already captured value with
  // `undefined`: the initial URL is the truth.
  onMount(() => {
    const captured = captureNow();
    if (captured !== undefined) {
      setInitial(captured);
    }
  });

  return initial;
}
